<template>
  <b-form-group
    :id="id + '-group'"
    :label="label"
    :label-for="id"
    :state="!errors.has(id)"
    :invalid-feedback="errors.first(id)"
    class="form-amount"
  >
    <masked-input
      ref="formAmount"
      v-model="model"
      v-validate="{ rules: validationRules }"
      :mask="numberMask"
      :guide="false"
      :name="id"
      :required="required"
      :data-testing="dataTesting"
      type="text"
      autocomplete="off"
      class="form-amount__input"
      data-vv-validate-on="blur"
      @blur="onBlur"
      @keydown.enter="onBlur"
    />
  </b-form-group>
</template>

<script>
import { getNumberFromModel } from '@core/utils';
import { BFormGroup } from 'bootstrap-vue';
import MaskedInput from 'vue-text-mask';
import createNumberMask from 'text-mask-addons/dist/createNumberMask';

/**
 * Standard form amount field
 */
export default {
  name: 'FormAmount',
  components: {
    BFormGroup,
    MaskedInput
  },
  inject: {
    $validator: '$validator'
  },
  props: {
    /**
    * input id - it's used also as name attribute and for error handling
    */
    id: {
      type: String,
      required: true
    },
    /**
    * label for the bootstrap form group
    */
    label: {
      type: String,
      default: ''
    },
    /**
    * placeholder for input field
    */
    placeholder: {
      type: String,
      default: ''
    },
    /**
    * value to fill in data from the api
    */
    value: {
      type: Number,
      default: null
    },
    /**
    * required attribute
    */
    required: {
      type: Boolean,
      default: false
    },
    /**
     * Variable for cypress testing
     */
    dataTesting: {
      type: String,
      default: null
    }
  },

  data() {
    return {
      /**
       * @model
       */
      model: ''
    };
  },

  computed: {
    /**
     * Returns a mask for currency amount. Read more at:
     * https://github.com/text-mask/text-mask/tree/master/addons
     */
    numberMask() {
       return createNumberMask({
        prefix: '',
        thousandsSeparatorSymbol: '.',
        allowDecimal: true,
        decimalSymbol: ',',
        requireDecimal: true,
        integerLimit: 16
      });
    },
    validationRules() {
      return {
        required: this.required,
        is_not: '0,00'
      };
    },
  },

  // detect changes in store step data
  watch: {
    value(newVal) {
      if(this.model !== newVal ) this.model = this.getModelFromNumber(newVal);
    }
  },

  mounted() {
    this.model = this.getModelFromNumber(this.value);
  },

  methods: {
    /**
    * update model and validate field
    * @event onBlur
    */
    onBlur() {
      if(this.required) {
        this.$validator.validate(
          this.id
        );
      }

      this.$emit('onchange', { key: this.id, val: this.getNumberFromModel(this.model) });
    },

    /**
     * returns a formatted model from a Number input
     * @param {Number} value
     */
    getModelFromNumber(value) {
      let valueString = '' + value,
          valueFormatted = ',',
          beforeComma = '';

      if (valueString.indexOf('.') !== -1) {
        let temp = valueString.split('.');
        beforeComma = temp[0];
        valueFormatted += temp[1].length === 1 ? temp[1] + '0': temp[1];
      } else {
        beforeComma = valueString;
        valueFormatted += '00';
      }

      if (beforeComma.length > 3) {
        valueFormatted = beforeComma.slice(-3) + valueFormatted;
        beforeComma = beforeComma.slice(0, -3);

        while (beforeComma.length >= 1) {
          valueFormatted = beforeComma.slice(-3) + '.' + valueFormatted;
          beforeComma = beforeComma.slice(0, -3);
        }
      } else {
        valueFormatted = beforeComma + valueFormatted;
      }

      return valueFormatted;
    },

    /**
     * sanitize model
     * @param {String} value
     */
    getNumberFromModel(value) {
      return getNumberFromModel(value);
    }
  },
};

</script>

<style lang="scss">
  @import './FormAmount';
</style>
